// src/features/auth/authSlice.js
import { createSlice, createAsyncThunk, isRejectedWithValue } from '@reduxjs/toolkit';
import * as cognitoService from '../../services/cognitoService';
import { create } from 'zustand';
import type { PayloadAction } from '@reduxjs/toolkit';

interface SignUpResult {
  user: any;
  userSub: string;
  userConfirmed: boolean;
  codeDeliveryDetails: any;
  pendingConfirmationEmail: string;
}

interface AuthState {
  user: any;
  token: string | null;
  loading: boolean;
  cognito: SignUpResult | null;
  isAuthenticated: boolean;
  status: string;
  error: string | null;
  confirmationStatus: string;
  resendStatus: string;
  pendingConfirmationEmail: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  cognito: null,
  isAuthenticated: false,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  confirmationStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  resendStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  pendingConfirmationEmail: null, // Store email for confirmation flow
};

interface CreateCognitoPayload {
  given_name: string;
  family_name: string;
  practice_id: string;
  email: string;
  role: string;
  password: string;
}

// Async thunks
export const signUp = createAsyncThunk<SignUpResult, CreateCognitoPayload>(
  'auth/signUp',
  async (userData: CreateCognitoPayload, { rejectWithValue }) => {
    try {
      const result = await cognitoService.signUp(userData);
      return { 
        userSub: result.userSub,
        userConfirmed: result.userConfirmed,
        codeDeliveryDetails: result.codeDeliveryDetails,
        pendingConfirmationEmail: userData.email
      };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to sign up');
    }
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({ username, password }, { rejectWithValue }) => {
   
    try {
      const result = await cognitoService.signIn({ username, password });
      localStorage.setItem('token', result.idToken.jwtToken);
      localStorage.setItem('user', JSON.stringify(result.idToken.payload));

      return {
        token: result.idToken.jwtToken,
        user: { username }
      };

    } catch (error) {
      return rejectWithValue(error.message || 'Failed to sign in');
    }
  }
);

export const checkCurrentUser = createAsyncThunk(
  'auth/checkCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const cognitoUser = cognitoService.getCurrentUser();
      
      if (!cognitoUser) {
        return null;
      }
      
      return new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => {
          if (err) {
            reject(err);
            return;
          }
          
          resolve({
            token: session.getIdToken().getJwtToken(),
            user: { username: cognitoUser.getUsername() }
          });
        });
      });
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to get current user');
    }
  }
);

export const signOut = createAsyncThunk(
  'auth/signOut',
  async (_, { rejectWithValue }) => {
    try {
      cognitoService.signOut();
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to sign out');
    }
  }
);

export const confirmSignUp = createAsyncThunk(
  'auth/confirmSignUp',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { email, username, confirmationCode } = payload;
      
      // Use email from payload or fallback to stored pending email
      const emailToConfirm = email || username || getState().auth.pendingConfirmationEmail;
      
      if (!emailToConfirm) {
        throw new Error('Email/username is required for confirmation');
      }

      if (!confirmationCode || confirmationCode.trim().length === 0) {
        throw new Error('Confirmation code is required');
      }

      // Validate confirmation code format (typically 6 digits)
      if (!/^\d{6}$/.test(confirmationCode.trim())) {
        throw new Error('Confirmation code must be 6 digits');
      }

      // ✅ CORRECT FORMAT - Pass parameters at top level
        const result = await cognitoService.confirmSignUp(
          emailToConfirm,                    // First parameter: username (email)
          confirmationCode.trim()            // Second parameter: code
        );
      

      
      return {
        ...result,
        confirmedEmail: emailToConfirm,
        message: 'Account confirmed successfully! You can now sign in.'
      };

    } catch (error) {
      console.error('Confirmation failed:', error);
      
      // Handle specific Cognito errors
      let errorMessage = 'Failed to confirm account';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid confirmation code. Please check and try again.';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Confirmation code has expired. Please request a new one.';
      } else if (error.code === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please wait before trying again.';
      } else if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found. Please check your email address.';
      } else if (error.code === 'NotAuthorizedException') {
        errorMessage = 'User is already confirmed or confirmation failed.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const resendConfirmationCode = createAsyncThunk(
  'auth/resendConfirmationCode',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { email, username } = payload || {};
      
      // Use email from payload or fallback to stored pending email
      const emailToResend = email || username || getState().auth.pendingConfirmationEmail;
      
      if (!emailToResend) {
        throw new Error('Email/username is required to resend confirmation code');
      }

      // Validate email format
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(emailToResend)) {
        throw new Error('Please provide a valid email address');
      }

      const result = await cognitoService.resendConfirmationCode(emailToResend);
      
      
      return {
        ...result,
        email: emailToResend,
        message: `Confirmation code sent to ${emailToResend}. Please check your email.`,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error('Failed to resend confirmation code:', error);
      
      // Handle specific Cognito errors
      let errorMessage = 'Failed to resend confirmation code';
      
      if (error.code === 'LimitExceededException') {
        errorMessage = 'Too many requests. Please wait before requesting another code.';
      } else if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found. Please check your email address.';
      } else if (error.code === 'InvalidParameterException') {
        errorMessage = 'Invalid email address format.';
      } else if (error.code === 'NotAuthorizedException') {
        errorMessage = 'User is already confirmed or cannot resend code.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

// Auto-signin after confirmation (optional)
export const confirmSignUpAndSignIn = createAsyncThunk(
  'auth/confirmSignUpAndSignIn',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { email, username, confirmationCode, password } = payload;
      
      // First confirm the signup
      const confirmResult = await dispatch(confirmSignUp({ 
        email, 
        username, 
        confirmationCode 
      })).unwrap();
      
      // If confirmation successful and password provided, auto sign in
      if (confirmResult && password) {
        const signinResult = await dispatch(signIn({ 
          username: email || username, 
          password 
        })).unwrap();
        
        return {
          confirmed: true,
          signedIn: true,
          user: signinResult.user,
          token: signinResult.token,
          message: 'Account confirmed and signed in successfully!'
        };
      }
      
      return {
        confirmed: true,
        signedIn: false,
        message: 'Account confirmed successfully! Please sign in.'
      };
      
    } catch (error) {
      return rejectWithValue(error || 'Failed to confirm and sign in');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearConfirmationError: (state) => {
      state.confirmationStatus = 'idle';
    },
    clearResendError: (state) => {
      state.resendStatus = 'idle';
    },
    setPendingConfirmationEmail: (state, action) => {
      state.pendingConfirmationEmail = action.payload;
    },
    resetAuthState: () => initialState,
    resetConfirmationState: (state) => {
      state.confirmationStatus = 'idle';
      state.resendStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // Sign Up
      .addCase(signUp.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        state.cognito = action.payload;
        state.pendingConfirmationEmail = action.payload.pendingConfirmationEmail;
        // We don't set user as authenticated after signup because they need to confirm email
      })
      .addCase(signUp.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign In
      .addCase(signIn.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.loading = false;
        state.error = null;
        // Clear confirmation state on successful sign in
        state.confirmationStatus = 'idle';
        state.resendStatus = 'idle';
        state.pendingConfirmationEmail = null;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      })
      
      // Check Current User
      .addCase(checkCurrentUser.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(checkCurrentUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loading = false;
        if (action.payload) {
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }
      })
      .addCase(checkCurrentUser.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      })
      
      // Sign Out
      .addCase(signOut.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(signOut.fulfilled, (state) => {
        // Reset to initial state on sign out
        return {
          ...initialState,
          status: 'succeeded'
        };
      })
      .addCase(signOut.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      })
      
      // Confirm Sign Up
      .addCase(confirmSignUp.pending, (state) => {
        state.confirmationStatus = 'loading';
        state.error = null;
      })
      .addCase(confirmSignUp.fulfilled, (state, action) => {
        state.confirmationStatus = 'succeeded';
        state.error = null;
        // Optionally clear pending email after successful confirmation
        // state.pendingConfirmationEmail = null;
      })
      .addCase(confirmSignUp.rejected, (state, action) => {
        state.confirmationStatus = 'failed';
        state.error = action.payload;
      })
      
      // Resend Confirmation Code
      .addCase(resendConfirmationCode.pending, (state) => {
        state.resendStatus = 'loading';
        state.error = null;
      })
      .addCase(resendConfirmationCode.fulfilled, (state, action) => {
        state.resendStatus = 'succeeded';
        state.error = null;
        // Update pending email if provided
        if (action.payload.email) {
          state.pendingConfirmationEmail = action.payload.email;
        }
      })
      .addCase(resendConfirmationCode.rejected, (state, action) => {
        state.resendStatus = 'failed';
        state.error = action.payload;
      })
      
      // Confirm and Sign In (combo action)
      .addCase(confirmSignUpAndSignIn.pending, (state) => {
        state.confirmationStatus = 'loading';
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmSignUpAndSignIn.fulfilled, (state, action) => {
        state.confirmationStatus = 'succeeded';
        state.error = null;
        
        if (action.payload.signedIn) {
          state.status = 'succeeded';
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.loading = false;
          state.pendingConfirmationEmail = null;
        } else {
          state.status = 'idle';
          state.loading = false;
        }
      })
      .addCase(confirmSignUpAndSignIn.rejected, (state, action) => {
        state.confirmationStatus = 'failed';
        state.status = 'failed';
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Enhanced Selectors
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectUser = (state) => state.auth.user;
export const selectToken = (state) => state.auth.token;
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthLoading = (state) => state.auth.loading;

// Confirmation-specific selectors
export const selectConfirmationStatus = (state) => state.auth.confirmationStatus;
export const selectResendStatus = (state) => state.auth.resendStatus;
export const selectPendingConfirmationEmail = (state) => state.auth.pendingConfirmationEmail;
export const selectIsConfirming = (state) => state.auth.confirmationStatus === 'loading';
export const selectIsResending = (state) => state.auth.resendStatus === 'loading';

// Composite selectors
export const selectConfirmationState = (state) => ({
  status: state.auth.confirmationStatus,
  isLoading: state.auth.confirmationStatus === 'loading',
  isSuccess: state.auth.confirmationStatus === 'succeeded',
  isError: state.auth.confirmationStatus === 'failed',
  email: state.auth.pendingConfirmationEmail,
  error: state.auth.error
});

export const selectResendState = (state) => ({
  status: state.auth.resendStatus,
  isLoading: state.auth.resendStatus === 'loading',
  isSuccess: state.auth.resendStatus === 'succeeded',
  isError: state.auth.resendStatus === 'failed',
  error: state.auth.error
});

// Actions
export const { 
  clearError, 
  clearConfirmationError,
  clearResendError,
  setPendingConfirmationEmail,
  resetAuthState,
  resetConfirmationState
} = authSlice.actions;

// Reducer
export default authSlice.reducer;
